// ─────────────────────────────────────────────
//  Team contacts — the SHAPE layer of the Team page.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Three live collections — `staff`, `surgeons`, `facilities` — become ONE
//  array of contact rows here, so the page never learns three vocabularies and
//  the search never learns three record shapes. Pure: no React, no Firebase.
//
//  ⚠️ THE SHEET IS GONE. Until 2026-08-19 this page rendered ONE mirrored
//  document copied out of a world-readable Google Sheet. ATC owns the data now,
//  which is why the CRNA-vs-AA split below is real rather than a heading that
//  admits it cannot tell: `staff.role` says "crna" or "aa" and we believe it.
//
//  ⚠️ SUPPRESS SYNTHETIC EMAILS. domain/email.js MINTS
//  `firstname.lastname@envisionhealth.com` so the seeders and the admin "Fix
//  emails" tool never drift. Those addresses were never inboxes. If a stored
//  address equals the form this app would have derived for that person, this
//  module returns NO email and the row renders without a Email link. A
//  `mailto:` that opens a compose window and silently goes nowhere is worse
//  than a blank space: the sender believes the message was sent.
//
//  ⚠️ `listed === false` HIDES A RECORD, FOR EVERYONE — and the test is against
//  `false`, never truthiness. Absent means listed, so the thousands of records
//  written before the field existed cannot vanish the day it ships.
// ─────────────────────────────────────────────
import { staffEmail } from "./email";
// ⚠️ THE FIELD RULES HAVE ONE OWNER, AND IT IS NOT THIS FILE. domain/contact.js
// is where "which key holds the phone" (`phone` on people, `phoneNumber` on the
// new shapes) and "absent means listed" are decided, for every surface. A
// second copy of a policy is not a backup — it is a future disagreement. This
// module composes those primitives and adds the ONE thing that is the
// directory's own business: refusing to print an address the app invented.
import {
  contactEmail as storedEmail, contactPhone, contactPhoneDisplay, isListed,
} from "./contact";
import { normalizeSurgeon } from "./surgeons";

// ── The sections, in reading order ────────────
// People first, split by role (ATC owns the roster, so the split is real),
// then surgeons, then the buildings themselves.
//
// "other" is a catch-all for a `staff.role` this build has never heard of:
// fail OPEN and show the person rather than dropping a colleague's number on
// the floor because someone added a role string in Setup. It is deliberately
// NOT offered as a filter chip — it is a diagnostic bucket, not a category
// anyone thinks in.
export const CONTACT_SECTIONS = [
  { id: "anesthesiologist", label: "Anesthesiologists" },
  { id: "crna",             label: "CRNAs" },
  { id: "aa",               label: "AAs" },
  { id: "resident",         label: "Residents" },
  { id: "other",            label: "Other team members" },
  { id: "surgeon",          label: "Surgeons" },
  { id: "facility",         label: "Facilities" },
];

export const SECTION_LABEL = Object.fromEntries(
  CONTACT_SECTIONS.map((s) => [s.id, s.label])
);

// The chips offered as filters — everything except the diagnostic bucket.
export const FILTERABLE_SECTION_IDS = CONTACT_SECTIONS
  .filter((s) => s.id !== "other")
  .map((s) => s.id);

// `staff.role` (the staff vocabulary, see domain/staff.js) → section id.
const STAFF_ROLE_SECTION = {
  doctor: "anesthesiologist",
  crna: "crna",
  aa: "aa",
  resident: "resident",
};

/** Which section a staff record renders under. Never returns null. */
export function sectionForStaffRole(role) {
  return STAFF_ROLE_SECTION[String(role || "").toLowerCase()] || "other";
}

// ── Text folding ──────────────────────────────
// Lowercase, strip accents, collapse whitespace. Applied to BOTH sides of every
// comparison, so "Nunez" finds "Nunez" and vice versa. Defined here rather
// than in teamDirectory because this is the lower module; teamDirectory
// re-exports it for the callers that have always imported it from there.
export function foldText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Digits only — both sides of a phone comparison go through this. */
export function digitsOf(value) {
  return String(value || "").replace(/\D/g, "");
}

const fold = foldText;

// ── Links ─────────────────────────────────────
/**
 * The COMPLETE href a Call control uses: "tel:+1" + ten digits, or null when
 * there is nothing dialable.
 *
 * ⚠️ IT RETURNS THE WHOLE URI, SCHEME INCLUDED. `href={`tel:${telHref(p)}`}`
 * yields "tel:tel:+1…", which renders as a perfectly normal link and does
 * nothing at all when a clinician taps it — invisible to the eye and to a
 * loose assertion. Tolerant of formatted and country-coded input so no caller
 * can accidentally build "tel:+1(813) 555-0147".
 *
 * ⚠️ THIS IS THE TEAM PAGE'S OWN COPY, ON PURPOSE. The identical helper lives
 * in features/team-directory/phone.js beside the SHEET PARSERS, which are
 * being retired with the sync. Dialing a number is not a spreadsheet concern,
 * so the surviving surface owns it here and does not depend on a folder that
 * is scheduled for deletion.
 */
export function telHref(phone) {
  let digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") digits = digits.slice(1);
  return digits.length === 10 ? `tel:+1${digits}` : null;
}

// Deliberately strict rather than clever. An address that cannot be parsed is
// an address that will not deliver, and a `mailto:` built from it opens a
// compose window addressed to nobody.
const ADDRESS = /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i;

/** "mailto:someone@example.com", or null when there is nothing to write to. */
export function mailtoHref(email) {
  const trimmed = String(email ?? "").trim();
  return ADDRESS.test(trimmed) ? `mailto:${trimmed}` : null;
}

// ── Names ─────────────────────────────────────
// Titles this app writes in front of a name — the doctor seeder stores
// "Dr. Nora Hale" — and credentials it writes behind one. Both are stripped
// before a name is split, because neither is part of a derived address.
const TITLES = new Set(["dr", "dr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "prof", "prof."]);

/** The name's own words, title and post-comma credentials removed. */
export function nameTokens(name) {
  const beforeComma = String(name || "").split(",")[0];
  const words = beforeComma.split(/\s+/).filter(Boolean);
  while (words.length > 1 && TITLES.has(words[0].toLowerCase())) words.shift();
  return words;
}

/**
 * Every address THIS APP would have minted for this person.
 *
 * A staff record carries one `name` field, not a first and a last, so the split
 * point is a guess — and a wrong guess would let a synthetic address through.
 * So every split is a candidate: "Mary Jane O'Brien-Smith" yields both
 * `mary.janeobriensmith@` and `maryjane.obriensmith@`. Cheap (a name is two or
 * three words) and it removes the guess entirely.
 */
export function derivedEmails(record) {
  const out = new Set();
  const add = (first, last) => {
    const derived = staffEmail(first, last);
    if (derived) out.add(derived);
  };
  // A record that carries the parts the seeder used gets the exact form first.
  if (record?.firstName || record?.lastName) add(record.firstName, record.lastName);

  const words = nameTokens(record?.name);
  if (words.length === 1) {
    add(words[0], "");
    add("", words[0]);
  }
  for (let i = 1; i < words.length; i += 1) {
    add(words.slice(0, i).join(" "), words.slice(i).join(" "));
  }
  return out;
}

/**
 * Is this stored address one domain/email.js invented rather than one a person
 * reads? See the file header for why that must render as nothing at all.
 */
export function isSyntheticEmail(email, record) {
  const stored = String(email ?? "").trim().toLowerCase();
  if (!stored) return false;
  return derivedEmails(record).has(stored);
}

/**
 * The address to PUT ON A ROW: "" when there is none, when it could not
 * deliver, or when this app minted it.
 *
 * ⚠️ IT IS NOT domain/contact.contactEmail, AND THE DIFFERENCE IS THE POINT.
 * That one answers "what address is stored on this record" — the honest answer,
 * which ProfilePage and the seeders need. This one answers "what address may
 * this page offer as a link", which is narrower: a synthesized address is
 * stored, and must still never become a `mailto:`.
 */
export function directoryEmail(record) {
  const stored = storedEmail(record);
  if (!ADDRESS.test(stored)) return "";
  return isSyntheticEmail(stored, record) ? "" : stored;
}

// ── Facility scope (contract §7) ──────────────
/**
 * The facility ids this account is entitled to see people from.
 *
 * ⚠️ THIS IS A DISPLAY SCOPE, NOT THE ACCESS DECISION. The access decision is
 * canSeeTeamDirectory() plus the Firestore rules, which are the only thing that
 * actually protects anything. This is the belt: a record stamped with a
 * facility this account cannot even see is not rendered.
 *
 * Derived from the facilities the account can READ, union its own membership,
 * which makes it environment-aware for free: in the sandbox (a local data
 * plane) the only facility is "sbx", so sandbox rows are the only rows; in a
 * live tenant it is that tenant's campuses and there is no other tenant in the
 * project to reach.
 *
 * ⚠️ IT DOES NOT ENFORCE WHEN IT KNOWS NOTHING. If the facilities read is
 * refused or the collection is empty, `enforced` is false and scoping is
 * skipped — otherwise a denied secondary read would silently empty the People
 * section and the page would claim nobody works here.
 */
export function facilityScope(auth, facilities) {
  const ids = new Set();
  for (const f of Array.isArray(facilities) ? facilities : []) {
    if (f && f.id) ids.add(f.id);
  }
  for (const id of auth?.user?.facilityIds || []) {
    if (id) ids.add(id);
  }
  return { ids, enforced: ids.size > 0 };
}

/**
 * Is this record inside the scope? A record with NO facility stamp is shared
 * tenant-wide — `staff` has always been one pool drawn on by every campus — so
 * it is in scope by definition, not hidden for lacking a field.
 */
export function inFacilityScope(record, scope) {
  if (!scope?.enforced) return true;
  const fid = record?.facilityId || record?.primaryFacilityId || null;
  return !fid || scope.ids.has(fid);
}

// ── Rows ──────────────────────────────────────
// One row shape for all three collections, so the page and the search read a
// person, a surgeon and a building the same way.
//
//   id        unique across sections (the collections have their own id spaces)
//   sectionId which heading it renders under
//   name      what is printed
//   phone     the digits to dial ("" when there is nothing)
//   phoneText what is printed on the Call control
//   email     "" when absent, unusable, or synthesized — see directoryEmail
//   meta      the one secondary fact this KIND of row carries: a surgeon's
//             specialty, a facility's address. null for people.
//   sortName  surname-first folded key, so a directory reads like a directory
function sortNameFor(name) {
  const words = nameTokens(name);
  if (words.length === 0) return fold(name);
  return `${fold(words[words.length - 1])} ${fold(words.join(" "))}`;
}

function row(id, sectionId, record, meta) {
  return {
    id,
    sectionId,
    name: String(record?.name || "").trim(),
    firstName: String(record?.firstName || ""),
    lastName: String(record?.lastName || ""),
    // The digits to dial. contactPhone knows which key holds them on each of
    // the three record kinds; stripping punctuation is this row's business.
    phone: digitsOf(contactPhone(record)),
    phoneText: contactPhoneDisplay(record),
    email: directoryEmail(record),
    meta: meta || null,
    sortName: sortNameFor(record?.name),
  };
}

/**
 * The three collections → the rows the page renders, in section order and
 * alphabetical by surname within a section.
 *
 * Every input is a plain array of `{ id, ...docData }`. Records that are
 * unlisted, out of facility scope, or nameless are dropped — a row with no name
 * is a phone number nobody can act on.
 */
export function buildContacts({ staff = [], surgeons = [], facilities = [], auth = null } = {}) {
  const scope = facilityScope(auth, facilities);
  const keep = (r) => r && isListed(r) && inFacilityScope(r, scope);
  const rows = [];

  for (const person of Array.isArray(staff) ? staff : []) {
    if (!keep(person) || !person.id) continue;
    const built = row(`staff:${person.id}`, sectionForStaffRole(person.role), person, null);
    if (built.name) rows.push(built);
  }

  // ⚠️ THROUGH normalizeSurgeon, NOT STRAIGHT OFF THE DOCUMENT. domain/surgeons
  // owns the shape of a surgeon record; a second reading of it here is how the
  // Setup panel and this page start disagreeing about what a surgeon has.
  for (const raw of Array.isArray(surgeons) ? surgeons : []) {
    const surgeon = normalizeSurgeon(raw);
    if (!surgeon || !keep(surgeon)) continue;
    const specialty = String(surgeon.specialty || "").trim();
    const built = row(`surgeon:${surgeon.id}`, "surgeon", surgeon, specialty || null);
    if (built.name) rows.push(built);
  }

  for (const facility of Array.isArray(facilities) ? facilities : []) {
    if (!facility || !facility.id || !isListed(facility)) continue;
    // A facility is its own scope — it IS one of the buildings this account can
    // see, which is what the scope was derived from.
    const address = String(facility.address || "").trim();
    const named = { ...facility, name: facility.name || facility.label || facility.id };
    const built = row(`facility:${facility.id}`, "facility", named, address || null);
    if (built.name) rows.push(built);
  }

  const order = Object.fromEntries(CONTACT_SECTIONS.map((s, i) => [s.id, i]));
  return rows.sort((a, b) => {
    const sa = order[a.sectionId] ?? 99;
    const sb = order[b.sectionId] ?? 99;
    if (sa !== sb) return sa - sb;
    return a.sortName.localeCompare(b.sortName);
  });
}
